import type { SelectFile } from '@package/drizzle/schema'

/**
 * Constructs the S3 object key (file path) from a file record.
 * The key format depends on whether a relative key prefix is present.
 *
 * @param fileRecord - The file record from the database
 * @returns The complete S3 object key in the format: `{relativeKey?}{fileId}.{extension}`
 *
 * @remarks
 * - If `relativeKey` is provided and ends with `/`, it's used as-is
 * - If `relativeKey` is provided without trailing `/`, one is added automatically
 * - If no `relativeKey`, the key is simply `{fileId}.{extension}`
 * - The relative key is useful for organizing files into folders/prefixes in S3
 *
 * @example
 * // File with relative key ending in '/'
 * const file1 = { id: 'abc123', extension: 'jpg', relativeKey: 'covers/' }
 * buildS3ObjectKey(file1)
 * // Returns: 'covers/abc123.jpg'
 *
 * @example
 * // File with relative key without trailing slash
 * const file2 = { id: 'def456', extension: 'png', relativeKey: 'thumbnails' }
 * buildS3ObjectKey(file2)
 * // Returns: 'thumbnails/def456.png'
 *
 * @example
 * // File without relative key
 * const file3 = { id: 'xyz789', extension: 'pdf', relativeKey: null }
 * buildS3ObjectKey(file3)
 * // Returns: 'xyz789.pdf'
 *
 * @example
 * // File with nested relative key
 * const file4 = { id: 'ghi012', extension: 'mp3', relativeKey: 'audio/lessons/' }
 * buildS3ObjectKey(file4)
 * // Returns: 'audio/lessons/ghi012.mp3'
 */
export function buildS3ObjectKey(fileRecord: SelectFile): string {
  const fileName = `${fileRecord.id}.${fileRecord.extension}`

  // No relative key - file is at bucket root
  if (!fileRecord.key) {
    return fileName
  }

  return fileRecord.key
}
